import { Controller, useFormContext } from "react-hook-form";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ValidAgeRanges, ValidGender } from "@/components/common/demographics";

import { CountryCombobox } from "@/components/country/country-combobox";
import { Input } from "@/components/ui/input";
import { LanguageCombobox } from "@/components/language/language-combobox";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const DemographicsFormFields = {
  firstName: z.string().min(1, "pages:demographics.firstNameRequired"),
  lastName: z.string().min(1, "pages:demographics.lastNameRequired"),
  gender: z.enum(ValidGender, {
    error: () => "pages:demographics.genderInvalid",
  }),
  ageRange: z.enum(ValidAgeRanges, {
    error: () => "pages:demographics.ageRangeInvalid",
  }),
  country: z.string().min(1, "pages:demographics.countryRequired"),
  nativeLanguage: z
    .string()
    .min(1, "pages:demographics.nativeLanguageRequired"),
};

export const DemographicFormSchema = z.object(DemographicsFormFields);
export type DemographicFormValues = z.infer<typeof DemographicFormSchema>;

export default function DemographicsForm() {
  const { t } = useTranslation(["pages", "common"]);
  const { control } = useFormContext(); // Access the form context from parent

  return (
    <FieldGroup className="space-y-6">
      <div className="grid grid-cols-2 gap-2 sm:gap-6 sm:grid-cols-2">
        <Controller
          name="firstName"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="form-user-info-firstName" className="inline">
                {t("pages:demographics.firstName")}*
              </FieldLabel>
              <Input
                {...field}
                id="form-user-info-firstName"
                aria-invalid={fieldState.invalid}
                placeholder={t("pages:demographics.firstNamePlaceholder")}
                autoComplete="off"
                className={fieldState.invalid ? "border-destructive" : ""}
              />
              {fieldState.invalid && (
                <FieldError
                  errors={[{ message: t(fieldState.error?.message || "") }]}
                />
              )}
            </Field>
          )}
        />
        <Controller
          name="lastName"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="form-user-info-lastName" className="inline">
                {t("pages:demographics.lastName")}*
              </FieldLabel>
              <Input
                {...field}
                id="form-user-info-lastName"
                aria-invalid={fieldState.invalid}
                placeholder={t("pages:demographics.lastNamePlaceholder")}
                autoComplete="off"
                className={fieldState.invalid ? "border-destructive" : ""}
              />
              {fieldState.invalid && (
                <FieldError
                  errors={[{ message: t(fieldState.error?.message || "") }]}
                />
              )}
            </Field>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-6 sm:grid-cols-2">
        <Controller
          name="gender"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="form-user-info-gender" className="inline">
                {t("pages:demographics.gender")}*
              </FieldLabel>
              <Select
                value={field.value as string}
                onValueChange={field.onChange}
              >
                <SelectTrigger
                  className={fieldState.invalid ? "border-destructive" : ""}
                >
                  <SelectValue
                    placeholder={t("pages:demographics.genderPlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {ValidGender?.map((gender) => (
                    <SelectItem value={gender} key={gender}>
                      {t(`pages:demographics.genderOptions.${gender}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldState.invalid && (
                <FieldError
                  errors={[{ message: t(fieldState.error?.message || "") }]}
                />
              )}
            </Field>
          )}
        />

        <Controller
          name="ageRange"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="form-user-info-ageRange" className="inline">
                {t("pages:demographics.ageRange")}*
              </FieldLabel>
              <Select
                value={field.value as string}
                onValueChange={field.onChange}
              >
                <SelectTrigger
                  className={fieldState.invalid ? "border-destructive" : ""}
                >
                  <SelectValue
                    placeholder={t("pages:demographics.ageRangePlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {ValidAgeRanges?.map((ageRange) => (
                    <SelectItem value={ageRange} key={ageRange}>
                      {t(`pages:demographics.ageRangeOptions.${ageRange}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldState.invalid && (
                <FieldError
                  errors={[{ message: t(fieldState.error?.message || "") }]}
                />
              )}
            </Field>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-6 sm:grid-cols-2">
        <Controller
          name="country"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="form-user-info-country" className="inline">
                {t("pages:demographics.country")}*
              </FieldLabel>
              <CountryCombobox
                value={field.value as string}
                onValueChange={field.onChange}
                isError={fieldState.invalid}
              />
              {fieldState.invalid && (
                <FieldError
                  errors={[{ message: t(fieldState.error?.message || "") }]}
                />
              )}
            </Field>
          )}
        />

        <Controller
          name="nativeLanguage"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel
                htmlFor="form-user-info-nativeLanguage"
                className="inline"
              >
                {t("pages:demographics.nativeLanguage")}*
              </FieldLabel>
              <LanguageCombobox
                value={field.value as string}
                onValueChange={field.onChange}
                isError={fieldState.invalid}
                type="native"
              />
              {fieldState.invalid && (
                <FieldError
                  errors={[{ message: t(fieldState.error?.message || "") }]}
                />
              )}
            </Field>
          )}
        />
      </div>
    </FieldGroup>
  );
}
